/**
 * Admin Price Management: list customers, search (incl. RAW NO), and edit customer_price.
 * Pattern mirrors AVV Discount/Price Management; unpaid invoices regenerate on save.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeftIcon, ChevronRightIcon, CurrencyDollarIcon, PencilIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import AdminLayout from './AdminLayout';
import { supabase } from '../context/AuthContext';

const ITEMS_PER_PAGE = 50;
const modalBackdropClassName =
  'fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]';

const hasText = (value) => String(value ?? '').trim() !== '';

const Dash = () => (
  <span className="text-slate-800" aria-label="Not provided">
    --
  </span>
);

const ValueOrDash = ({ value }) => {
  const text = String(value ?? '').trim();
  return text ? text : <Dash />;
};

const normalizeRole = (role) => {
  const value = String(role || 'customer').trim().toLowerCase();
  if (value === 'owner' || value === 'admin') return value;
  return 'customer';
};

const normalizePricingMode = (mode) => {
  const value = String(mode || 'standard').trim().toLowerCase();
  return value === 'flat_per_report' ? 'flat_per_report' : 'standard';
};

const ROLE_META = {
  owner: {
    label: 'Owner',
    className: 'bg-violet-50 text-violet-700 ring-violet-200',
    dotClassName: 'bg-violet-500',
  },
  admin: {
    label: 'Admin',
    className: 'bg-orange-50 text-orange-700 ring-orange-200',
    dotClassName: 'bg-orange-500',
  },
  customer: {
    label: 'Customer',
    className: 'bg-sky-50 text-sky-700 ring-sky-200',
    dotClassName: 'bg-sky-500',
  },
};

const PRICING_MODE_META = {
  standard: {
    label: 'Standard',
    className: 'bg-sky-50 text-sky-700 ring-sky-200',
    dotClassName: 'bg-sky-500',
  },
  flat_per_report: {
    label: 'Flat Per Report',
    className: 'bg-orange-50 text-orange-700 ring-orange-200',
    dotClassName: 'bg-orange-500',
  },
};

const RoleBadge = ({ role }) => {
  const meta = ROLE_META[normalizeRole(role)];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-semibold tracking-wide ring-1 ring-inset sm:text-xs ${meta.className}`}
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${meta.dotClassName}`} aria-hidden="true" />
      {meta.label}
    </span>
  );
};

const PricingModeBadge = ({ mode }) => {
  const meta = PRICING_MODE_META[normalizePricingMode(mode)];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-semibold tracking-wide ring-1 ring-inset sm:text-xs ${meta.className}`}
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${meta.dotClassName}`} aria-hidden="true" />
      {meta.label}
    </span>
  );
};

const DetailField = ({ label, children, className = '' }) => (
  <div className={`min-w-0 rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-3 ${className}`}>
    <dt className="text-xs font-medium uppercase tracking-wider text-gray-500">
      {label}
    </dt>
    <dd className="mt-1.5 min-w-0 break-words text-xs font-normal text-gray-900">
      {children}
    </dd>
  </div>
);

const PriceManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchBy, setSearchBy] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [pricingModeFilter, setPricingModeFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultCustomerPrice, setDefaultCustomerPrice] = useState(null);
  const [defaultPriceInput, setDefaultPriceInput] = useState('');
  const [isSavingDefault, setIsSavingDefault] = useState(false);
  const [defaultPriceError, setDefaultPriceError] = useState('');
  const [defaultPriceNotice, setDefaultPriceNotice] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editPricingMode, setEditPricingMode] = useState('standard');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successDetails, setSuccessDetails] = useState(null);
  const [openDropdownIdx, setOpenDropdownIdx] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchUsersAndDefaultPrice = async () => {
      setLoading(true);

      const [usersResult, defaultPriceResult] = await Promise.all([
        supabase
          .from('users')
          .select('id, name, email, phone, raw, customer_price, role, pricing_mode')
          .order('name', { ascending: true }),
        supabase
          .from('default_customer_price')
          .select('price')
          .limit(1)
          .maybeSingle(),
      ]);

      if (defaultPriceResult.error) {
        console.error('Failed to load default_customer_price:', defaultPriceResult.error);
      } else if (defaultPriceResult.data?.price != null) {
        const price = Number(defaultPriceResult.data.price);
        setDefaultCustomerPrice(price);
        setDefaultPriceInput(Number.isFinite(price) ? price.toFixed(2) : '');
      }

      if (!usersResult.error && usersResult.data) {
        // New signups often have only email and phone. Keep anyone who can be identified.
        setUsers(
          usersResult.data.filter(
            (u) => hasText(u.name) || hasText(u.email) || hasText(u.phone)
          )
        );
      } else {
        console.error('Failed to load users for price management:', usersResult.error);
        setUsers([]);
      }
      setLoading(false);
    };

    fetchUsersAndDefaultPrice();
  }, []);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return users.filter((item) => {
      const role = normalizeRole(item.role);
      if (roleFilter !== 'all' && role !== roleFilter) return false;

      const pricingMode = normalizePricingMode(item.pricing_mode);
      if (pricingModeFilter !== 'all' && pricingMode !== pricingModeFilter) return false;

      if (!term) return true;

      const name = (item.name || '').toLowerCase();
      const email = (item.email || '').toLowerCase();
      const phone = (item.phone || '').toLowerCase();
      const raw = (item.raw || '').toLowerCase();

      if (searchBy === 'name') return name.includes(term);
      if (searchBy === 'email') return email.includes(term);
      if (searchBy === 'phone') return phone.includes(term);
      if (searchBy === 'raw') return raw.includes(term);
      return (
        name.includes(term) ||
        email.includes(term) ||
        phone.includes(term) ||
        raw.includes(term)
      );
    });
  }, [users, searchTerm, searchBy, roleFilter, pricingModeFilter]);

  // Filters run on the full in-memory list; pagination only slices the filtered result.
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ITEMS_PER_PAGE));

  useEffect(() => {
    setCurrentPage(1);
    setOpenDropdownIdx(null);
  }, [searchTerm, searchBy, roleFilter, pricingModeFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredUsers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredUsers, currentPage]);

  const pageNumbers = useMemo(() => {
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    const numbers = [];
    for (let i = startPage; i <= endPage; i++) {
      numbers.push(i);
    }
    return { numbers, startPage, endPage };
  }, [currentPage, totalPages]);

  const handleDropdownClick = (idx) => {
    setOpenDropdownIdx(openDropdownIdx === idx ? null : idx);
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditPrice(
      typeof user.customer_price === 'number'
        ? String(user.customer_price)
        : user.customer_price != null
          ? String(user.customer_price)
          : defaultCustomerPrice != null
            ? String(defaultCustomerPrice)
            : ''
    );
    setEditPricingMode(normalizePricingMode(user.pricing_mode));
    setError('');
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditingUser(null);
    setEditPrice('');
    setEditPricingMode('standard');
    setError('');
  };

  const handleSave = async () => {
    setError('');
    setIsSaving(true);

    try {
      const parsed = parseFloat(editPrice);
      if (Number.isNaN(parsed) || parsed < 0) {
        throw new Error('Enter a valid user price (0 or greater).');
      }

      const pricingMode = normalizePricingMode(editPricingMode);

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/update-customer-price`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken || process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: editingUser.id,
            new_price: parsed,
            pricing_mode: pricingMode,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update user price');
      }

      const result = await response.json().catch(() => ({}));

      setUsers((prev) =>
        prev.map((u) =>
          u.id === editingUser.id
            ? { ...u, customer_price: parsed, pricing_mode: pricingMode }
            : u
        )
      );

      const userName = editingUser.name || editingUser.email || 'This user';
      setIsModalOpen(false);
      setEditingUser(null);
      setEditPrice('');
      setEditPricingMode('standard');
      setSuccessDetails({
        userName,
        price: parsed.toFixed(2),
        pricingMode: PRICING_MODE_META[pricingMode].label,
        remainingInBackground: Number(result.remainingInBackground) || 0,
      });
      setShowSuccessModal(true);
    } catch (saveError) {
      console.error('Error updating user price:', saveError);
      setError(saveError.message || 'Failed to update user price. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDefaultPrice = async (event) => {
    event.preventDefault();
    setDefaultPriceError('');
    setDefaultPriceNotice('');
    setIsSavingDefault(true);

    try {
      const parsed = Math.round(parseFloat(defaultPriceInput) * 100) / 100;
      if (Number.isNaN(parsed) || parsed < 0) {
        throw new Error('Enter a valid default price (0 or greater).');
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) {
        throw new Error('Sign in again to change the default price.');
      }

      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/update-default-customer-price`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ price: parsed }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update the default price');
      }

      const result = await response.json().catch(() => ({}));
      const saved = Number(result.price);
      const nextPrice = Number.isFinite(saved) ? saved : parsed;
      setDefaultCustomerPrice(nextPrice);
      setDefaultPriceInput(nextPrice.toFixed(2));
      setDefaultPriceNotice(`Saved. New users will start at $${nextPrice.toFixed(2)}.`);
    } catch (saveError) {
      console.error('Error updating default customer price:', saveError);
      setDefaultPriceError(saveError.message || 'Failed to update the default price. Please try again.');
    } finally {
      setIsSavingDefault(false);
    }
  };

  const renderPrice = (price) => {
    if (typeof price === 'number') return `$${price.toFixed(2)}`;
    if (price != null && price !== '' && !Number.isNaN(Number(price))) {
      return `$${Number(price).toFixed(2)}`;
    }
    return '- Needs to Set';
  };

  return (
    <AdminLayout
      activeItemId="price-management"
      title="Price Management"
      subtitle="View and manage user base prices. Changes apply to unpaid invoices only."
      contentClassName="w-full max-w-none"
    >
      <div className="price-important-banner mb-4 rounded-2xl border border-blue-300 bg-blue-50 p-4 md:mb-6">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon
            className="price-important-icon mt-0.5 h-5 w-5 shrink-0 text-blue-600"
            aria-hidden="true"
          />
          <p className="text-xs text-blue-900 sm:text-sm">
            <strong>Important:</strong> Updating a user&apos;s base price regenerates all unpaid invoices.
            Paid invoices are not changed.
          </p>
        </div>
      </div>

      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:mb-6">
        <form onSubmit={handleSaveDefaultPrice}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-slate-900">Default price for new users</h2>
              <p className="mt-1 text-xs leading-relaxed text-slate-500 sm:text-sm">
                New signups start at this amount
                {defaultCustomerPrice != null ? ` (currently $${defaultCustomerPrice.toFixed(2)})` : ''}.
                Existing users have their own prices and are not affected by this setting.
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <label htmlFor="default-new-user-price" className="sr-only">
                Default new user price in dollars
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
                  $
                </span>
                <input
                  id="default-new-user-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={defaultPriceInput}
                  onChange={(e) => {
                    setDefaultPriceInput(e.target.value);
                    setDefaultPriceError('');
                    setDefaultPriceNotice('');
                  }}
                  disabled={loading || isSavingDefault}
                  placeholder="330.00"
                  className="w-36 rounded-xl border border-slate-300 py-2 pl-7 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
                />
              </div>
              <button
                type="submit"
                disabled={loading || isSavingDefault}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isSavingDefault ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
          {defaultPriceError && (
            <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {defaultPriceError}
            </p>
          )}
          {defaultPriceNotice && (
            <p className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              {defaultPriceNotice}
            </p>
          )}
        </form>
      </div>

      <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm md:mb-6 md:p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-grow flex-col gap-3 sm:flex-row">
            <div className="relative flex-grow sm:flex-1">
              <input
                type="text"
                placeholder="Search customers..."
                className="w-full rounded-xl border border-slate-300 py-2 pl-10 pr-4 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 xl:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <select
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:w-auto sm:min-w-[140px] xl:text-sm"
              value={searchBy}
              onChange={(e) => setSearchBy(e.target.value)}
            >
              <option value="all">Search All</option>
              <option value="name">Name</option>
              <option value="email">Email</option>
              <option value="phone">Phone No.</option>
              <option value="raw">RAW No.</option>
            </select>
            <select
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:w-auto sm:min-w-[140px] xl:text-sm"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              aria-label="Filter by role"
            >
              <option value="all">All Roles</option>
              <option value="customer">Customer</option>
              <option value="owner">Owner</option>
              <option value="admin">Admin</option>
            </select>
            <select
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:w-auto sm:min-w-[160px] xl:text-sm"
              value={pricingModeFilter}
              onChange={(e) => setPricingModeFilter(e.target.value)}
              aria-label="Filter by pricing mode"
            >
              <option value="all">All Pricing Modes</option>
              <option value="standard">Standard</option>
              <option value="flat_per_report">Flat Per Report</option>
            </select>
          </div>
          <div className="flex flex-shrink-0 items-center justify-center gap-2 text-xs text-gray-600 md:justify-end xl:text-sm">
            <span>Found:</span>
            <span className="font-medium">{filteredUsers.length}</span>
            <span>results</span>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-sm md:p-4">
        <table className="min-w-full bg-white">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:hidden sm:px-4 sm:py-3" />
              <th className="hidden px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sm:table-cell sm:px-4 sm:py-3">S. No.</th>
              <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-4 sm:py-3">Name</th>
              <th className="hidden px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 lg:table-cell sm:px-4 sm:py-3">Email</th>
              <th className="hidden px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 md:table-cell sm:px-4 sm:py-3">Phone No.</th>
              <th className="hidden px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:table-cell sm:px-4 sm:py-3">RAW No.</th>
              <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-4 sm:py-3">User Price</th>
              <th className="hidden px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:table-cell sm:px-4 sm:py-3">Pricing Mode</th>
              <th className="hidden px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 xl:table-cell sm:px-4 sm:py-3">Role</th>
              <th className="px-2 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 sm:px-4 sm:py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="10" className="py-8 text-center text-xs text-gray-500 md:text-sm">
                  Loading...
                </td>
              </tr>
            ) : paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan="10" className="py-8 text-center text-xs text-gray-500 md:text-sm">
                  No users found.
                </td>
              </tr>
            ) : (
              paginatedUsers.map((item, idx) => {
                const serialNumber = (currentPage - 1) * ITEMS_PER_PAGE + idx + 1;
                return (
                <React.Fragment key={item.id}>
                  <tr
                    className="hover:bg-gray-50 max-xl:cursor-pointer"
                    onClick={() => {
                      if (window.innerWidth < 1280) {
                        handleDropdownClick(idx);
                      }
                    }}
                  >
                    <td className="px-2 py-2 text-xs xl:hidden sm:px-4 sm:py-4 xl:text-sm">
                      <button
                        type="button"
                        className="focus:outline-none"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDropdownClick(idx);
                        }}
                        aria-expanded={openDropdownIdx === idx}
                        aria-label={openDropdownIdx === idx ? 'Hide details' : 'Show details'}
                      >
                        <svg
                          className={`h-5 w-5 transition-transform duration-200 ${openDropdownIdx === idx ? 'rotate-90' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </td>
                    <td className="hidden break-words px-2 py-2 text-xs sm:table-cell sm:px-4 sm:py-4 xl:text-sm">{serialNumber}</td>
                    <td className="break-words px-2 py-2 text-xs sm:px-4 sm:py-4 xl:text-sm">
                      {hasText(item.name) ? (
                        item.name.trim()
                      ) : (
                        <div>
                          <Dash />
                          {(hasText(item.email) || hasText(item.phone)) && (
                            <p className="mt-0.5 break-all text-[11px] font-normal text-slate-500 lg:hidden">
                              {hasText(item.email) ? item.email.trim() : item.phone.trim()}
                            </p>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="hidden break-words px-2 py-2 text-xs lg:table-cell sm:px-4 sm:py-4 xl:text-sm">
                      <ValueOrDash value={item.email} />
                    </td>
                    <td className="hidden break-words px-2 py-2 text-xs md:table-cell sm:px-4 sm:py-4 xl:text-sm">
                      <ValueOrDash value={item.phone} />
                    </td>
                    <td className="hidden break-words px-2 py-2 text-xs xl:table-cell sm:px-4 sm:py-4 xl:text-sm">
                      <ValueOrDash value={item.raw} />
                    </td>
                    <td className="break-words px-2 py-2 text-xs sm:px-4 sm:py-4 xl:text-sm">
                      {renderPrice(item.customer_price)}
                    </td>
                    <td className="hidden px-2 py-2 xl:table-cell sm:px-4 sm:py-4">
                      <PricingModeBadge mode={item.pricing_mode} />
                    </td>
                    <td className="hidden px-2 py-2 xl:table-cell sm:px-4 sm:py-4">
                      <RoleBadge role={item.role} />
                    </td>
                    <td className="whitespace-nowrap px-2 py-2 sm:px-4 sm:py-4">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded-lg bg-blue-600 p-1.5 text-white hover:bg-blue-700 sm:px-4 sm:py-2 sm:text-sm"
                        aria-label="Edit"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleEditClick(item);
                        }}
                      >
                        <PencilIcon className="h-4 w-4 sm:hidden" aria-hidden="true" />
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                    </td>
                  </tr>
                  {openDropdownIdx === idx && (
                    <tr className="bg-slate-50/80 xl:hidden">
                      <td colSpan="10" className="px-2 py-2 sm:px-4 sm:py-3">
                        <dl className="grid grid-cols-2 gap-2.5 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:gap-3 sm:p-4">
                          <DetailField label="Email" className="lg:hidden">
                            <span className="break-all">
                              <ValueOrDash value={item.email} />
                            </span>
                          </DetailField>
                          <DetailField label="Phone" className="md:hidden">
                            <ValueOrDash value={item.phone} />
                          </DetailField>
                          <DetailField label="RAW No.">
                            <ValueOrDash value={item.raw} />
                          </DetailField>
                          <DetailField label="Pricing Mode">
                            <PricingModeBadge mode={item.pricing_mode} />
                          </DetailField>
                          <DetailField label="Role">
                            <RoleBadge role={item.role} />
                          </DetailField>
                        </dl>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>

        {!loading && filteredUsers.length > 0 && totalPages > 1 && (
          <div className="mt-3 flex flex-col gap-2 border-t border-slate-200 px-2 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-4 sm:py-3">
            <p className="text-[11px] text-slate-600 sm:text-xs xl:text-sm">
              Showing{' '}
              <span className="font-medium">
                {(currentPage - 1) * ITEMS_PER_PAGE + 1}
              </span>{' '}
              to{' '}
              <span className="font-medium">
                {Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)}
              </span>{' '}
              of <span className="font-medium">{filteredUsers.length}</span>
            </p>
            <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                type="button"
                onClick={() => {
                  setOpenDropdownIdx(null);
                  setCurrentPage((prev) => Math.max(prev - 1, 1));
                }}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md border border-slate-300 bg-white px-1.5 py-1 text-slate-400 hover:bg-slate-50 disabled:opacity-50 sm:rounded-l-lg sm:px-2 sm:py-1.5 md:rounded-l-[0.75rem] md:py-2"
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5" aria-hidden="true" />
              </button>

              {pageNumbers.startPage > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setOpenDropdownIdx(null);
                      setCurrentPage(1);
                    }}
                    className="relative inline-flex items-center border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 sm:px-3 sm:py-1.5 sm:text-xs md:px-4 md:py-2 md:text-sm"
                  >
                    1
                  </button>
                  {pageNumbers.startPage > 2 && (
                    <span className="relative inline-flex items-center border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 sm:px-3 sm:py-1.5 sm:text-xs md:px-4 md:py-2 md:text-sm">
                      ...
                    </span>
                  )}
                </>
              )}

              {pageNumbers.numbers.map((number) => (
                <button
                  key={number}
                  type="button"
                  onClick={() => {
                    setOpenDropdownIdx(null);
                    setCurrentPage(number);
                  }}
                  className={`relative inline-flex items-center border px-2 py-1 text-[11px] font-medium sm:px-3 sm:py-1.5 sm:text-xs md:px-4 md:py-2 md:text-sm ${
                    currentPage === number
                      ? 'z-10 border-blue-500 bg-blue-50 text-blue-600'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {number}
                </button>
              ))}

              {pageNumbers.endPage < totalPages && (
                <>
                  {pageNumbers.endPage < totalPages - 1 && (
                    <span className="relative inline-flex items-center border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 sm:px-3 sm:py-1.5 sm:text-xs md:px-4 md:py-2 md:text-sm">
                      ...
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setOpenDropdownIdx(null);
                      setCurrentPage(totalPages);
                    }}
                    className="relative inline-flex items-center border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 sm:px-3 sm:py-1.5 sm:text-xs md:px-4 md:py-2 md:text-sm"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                type="button"
                onClick={() => {
                  setOpenDropdownIdx(null);
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages));
                }}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center rounded-r-md border border-slate-300 bg-white px-1.5 py-1 text-slate-400 hover:bg-slate-50 disabled:opacity-50 sm:rounded-r-lg sm:px-2 sm:py-1.5 md:rounded-r-[0.75rem] md:py-2"
              >
                <span className="sr-only">Next</span>
                <ChevronRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5" aria-hidden="true" />
              </button>
            </nav>
          </div>
        )}
      </div>

      {isModalOpen && editingUser && createPortal(
        <div className={modalBackdropClassName}>
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:p-6">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-700">
                <CurrencyDollarIcon className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900">Edit Pricing</h3>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <p className="text-xs text-slate-500">
                    {hasText(editingUser.name) ? editingUser.name.trim() : <Dash />}
                  </p>
                  <RoleBadge role={editingUser.role} />
                </div>
                {!hasText(editingUser.name) && (hasText(editingUser.email) || hasText(editingUser.phone)) && (
                  <p className="mt-0.5 text-xs text-slate-400">
                    {[editingUser.email, editingUser.phone].filter(hasText).map((value) => value.trim()).join(' · ')}
                  </p>
                )}
              </div>
            </div>

            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              User Price ($)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={editPrice}
              onChange={(e) => setEditPrice(e.target.value)}
              className="mb-4 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
              placeholder={defaultCustomerPrice != null ? String(defaultCustomerPrice) : '0'}
            />

            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Pricing Mode
            </label>
            <select
              value={editPricingMode}
              onChange={(e) => setEditPricingMode(e.target.value)}
              className="mb-4 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              <option value="standard">Standard</option>
              <option value="flat_per_report">Flat Per Report</option>
            </select>

            {error && (
              <p className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancel}
                disabled={isSaving}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className="inline-flex items-center rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showSuccessModal && successDetails && createPortal(
        <div
          className={modalBackdropClassName}
          role="dialog"
          aria-modal="true"
          aria-labelledby="price-updated-title"
        >
          <div className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
            <div className="px-6 pb-1 pt-6 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircleIcon className="h-7 w-7 text-emerald-600" aria-hidden="true" />
              </div>
              <h3 id="price-updated-title" className="mt-4 text-lg font-semibold text-slate-900">
                Price updated
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                {successDetails.userName}&apos;s pricing is changed.
              </p>
            </div>

            <dl className="mx-6 mt-4 divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">User price</dt>
                <dd className="text-sm font-semibold text-slate-900">${successDetails.price}</dd>
              </div>
              <div className="flex items-center justify-between gap-4 px-4 py-3">
                <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">Pricing mode</dt>
                <dd className="text-sm font-semibold text-slate-900">{successDetails.pricingMode}</dd>
              </div>
            </dl>

            {successDetails.remainingInBackground > 0 ? (
              <p className="mx-6 mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs leading-relaxed text-blue-800">
                {successDetails.remainingInBackground} more unpaid invoice
                {successDetails.remainingInBackground === 1 ? ' is' : 's are'} being regenerated in the
                background and will appear over the next few minutes. Paid invoices were left unchanged.
              </p>
            ) : (
              <p className="mx-6 mt-3 text-xs leading-relaxed text-slate-500">
                Unpaid invoices were regenerated with the new pricing. Paid invoices were left unchanged.
              </p>
            )}

            <div className="p-6 pt-4">
              <button
                type="button"
                onClick={() => setShowSuccessModal(false)}
                className="w-full rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
              >
                Done
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </AdminLayout>
  );
};

export default PriceManagement;
